import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getEnquries, createUser } from '../../services/api/implementation/impl';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
// import filter from '../../../shared/assets/svg/filter.svg'; // if needed
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Table from '../../../shared/components/layout/Table';
import '../../../shared/styles/modal.scss';
import toast from 'react-hot-toast';
import { transformData } from '../../utils/transformData';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const Enquries = () => {
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page

  const userId = Cookies.get('userId');

  // Page view tracking
  useEffect(() => {
    mixpanel.track('Super Admin Enquries Viewed', {
      user_id: userId,
      page: page,
      timestamp: new Date().toISOString()
    });
  }, [userId, page]);

  // Query: fetch all users
  const {
    data: enquries,
    refetch: refetchUsers,
    isFetching
  } = useQuery({
    queryKey: ['enquries', page],
    queryFn: () => getEnquries(page, limit),
    keepPreviousData: true,
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Enquries Fetch Failed', {
          user_id: userId,
          page: page,
          limit: limit,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        toast.error(
          result?.response?.data?.message || result?.message || 'Failed to fetch enquries'
        );
        return;
      }

      mixpanel.track('Super Admin Enquries Fetch Success', {
        user_id: userId,
        page: page,
        limit: limit,
        enquries_count: result?.data?.length || 0,
        total_users: result?.meta?.total || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Enquries Fetch Failed', {
        user_id: userId,
        page: page,
        limit: limit,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch enquries');
    }
  });

  useEffect(() => {
    refetchUsers();
  }, []);

  // Mutation for creating user (approving enquiry)
  const { mutate: createUserMutation, isLoading: isApproving } = useMutation({
    mutationFn: (userData) => createUser(userData),
    onSuccess: (result) => {
      mixpanel.track('Super Admin Enquiry Approved Success', {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      toast.success('Enquiry approved successfully!');
      refetchUsers(); // Refresh the enquiries list
    },
    onError: (err) => {
      mixpanel.track('Super Admin Enquiry Approval Failed', {
        user_id: userId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error(err?.response?.data?.message || err?.message || 'Failed to approve enquiry');
    }
  });

  // total pages
  const totalPages = enquries?.meta ? Math.ceil(enquries?.meta.total / limit) : 1;

  // Build the columns for your table
  const enquriesDataFormat = ['id', 'name', 'email', 'phone_number', 'role', 'status'];

  // Transform for table consumption
  const transformedData = enquries?.map((el) =>
    transformData(
      {
        ...el,

        status: el.approved ? 'Approved' : 'Approval'
      },
      enquriesDataFormat
    )
  );

  // Handle pagination
  const handlePageChange = (newPage) => {
    mixpanel.track('Super Admin Enquries Pagination', {
      user_id: userId,
      from_page: page,
      to_page: newPage,
      timestamp: new Date().toISOString()
    });
    setPage(newPage);
  };

  const handleApprove = (id) => {
    // Find the enquiry data by id
    const enquiryData = enquries?.find((enquiry) => enquiry.id === Number(id));

    if (!enquiryData) {
      toast.error('Enquiry data not found');
      return;
    }

    // Track approval attempt
    mixpanel.track('Super Admin Enquiry Approval Attempted', {
      user_id: userId,
      enquiry_id: id,
      enquiry_name: enquiryData.name,
      enquiry_email: enquiryData.email,
      timestamp: new Date().toISOString()
    });

    // Create payload similar to AddBuilder.jsx for primary user
    const payload = {
      primary_username: enquiryData.name || '',
      primary_phone_number: enquiryData.phone_number || '',
      primary_email: enquiryData.email || ''
    };

    // Call the createUser API
    createUserMutation(payload);
  };

  return (
    <>
      <Layout>
        {/* Top Header */}
        <div className="fixed z-[20] top-0 left-[16.5vw] w-[83.5vw]">
          <Header data={{ name: 'Enquries' }} />
        </div>

        {/* Main Content - Table + Pagination */}
        <GridWrapper width={100}>
          <div className="mt-[5.5rem] h-[calc(100vh-7rem)] overflow-auto relative user-table-container pb-3">
            <style>{`
            .user-table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .user-table-container .table-container {
              height: auto !important;
            }
            .user-table-container > div {
              height: auto !important;
            }
          `}</style>
            <Table
              headers={['Name', 'Email', 'Phone', 'Role', 'Action']}
              data={transformedData}
              identifier={'id'}
              isEditEnable={false}
              onApprove={handleApprove}
            />

            <PaginationComponent
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isFetching={isFetching}
            />
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default Enquries;
